import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Ensure the browser never fights our custom scroll restoration.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

/**
 * Generic infinite-scroll list with:
 *  - debounced fetch on dependency change (search / filters / page)
 *  - race-condition guarded fetching (stale responses ignored)
 *  - sessionStorage persistence of the accumulated list + page + loaded pages
 *  - restoring that state when navigating back to the list
 *  - scrolling the previously-clicked card back into view on restore
 *  - resetting pagination when search/filters/an extra key (e.g. selected customer) changes
 *
 * @param {object} params
 * @param {string} params.storageKeyPrefix - e.g. "pagination:orders" or "pagination:products"
 * @param {string|number} [params.extraKey] - additional key segment (e.g. selected customer id) that should get its own storage bucket
 * @param {string} params.query - current search query (used as part of the storage signature)
 * @param {string} params.filtersSignature - stable stringified signature of applied filters
 * @param {(page: number) => Promise<{ rows: any[], total: number, perPage: number }>} params.fetchPage
 *        Must return the formatted rows for that page, the total count, and the perPage used.
 *        Throwing is treated as a failed fetch (caller should let it propagate or catch internally).
 */
export function useInfiniteListWithScrollRestore({ storageKeyPrefix, extraKey, query, filtersSignature, fetchPage }) {
  const [listData, setListData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  const loaderRef = useRef(null);
  const fetchIdRef = useRef(0);
  const hasRestoredStateRef = useRef(false);
  const isRestoringRef = useRef(false);
  const loadedPagesRef = useRef(new Set());
  const latestStateRef = useRef({ query, filtersSignature, page, listData, totalCount, hasMore });
  const pendingScrollIdRef = useRef(null);
  const clickedItemIdRef = useRef(null);

  const storageKey = useMemo(() => {
    const keySegment = extraKey ?? "default";
    return `${storageKeyPrefix}:${keySegment}`;
  }, [storageKeyPrefix, extraKey]);

  // Keep latestStateRef perfectly synced for use in the unmount-persist effect.
  useEffect(() => {
    latestStateRef.current = { query, filtersSignature, page, listData, totalCount, hasMore };
  });

  const fetchList = useCallback(async () => {
    if (loadedPagesRef.current.has(page) && listData.length > 0) {
      return;
    }

    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      setIsFetchingMore(true);
      if (page === 1) setLoading(true);

      const { rows, total, perPage } = await fetchPage(page);

      if (currentFetchId !== fetchIdRef.current) return;

      setListData((prev) => (page === 1 ? rows : [...prev, ...rows]));
      setTotalCount(total || 0);
      setHasMore(rows.length === perPage);
      loadedPagesRef.current.add(page);
    } catch (error) {
      if (currentFetchId !== fetchIdRef.current) return;
    } finally {
      if (currentFetchId !== fetchIdRef.current) return;
      setLoading(false);
      setIsFetchingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchPage]);

  const resetList = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    loadedPagesRef.current = new Set();
    clickedItemIdRef.current = null;
    pendingScrollIdRef.current = null;
    setPage(1);
    setListData([]);
    setTotalCount(0);
    setHasMore(true);
  }, [storageKey]);

  const pendingRestorePayloadRef = useRef(undefined);
  const restoreGraceTimerRef = useRef(null);

  // Read persisted state from sessionStorage on mount / when the storage key changes.
  //
  // IMPORTANT: Filters.jsx restores its own appliedFilters asynchronously, in a
  // separate effect, one render after mount. That means on the very first render
  // after navigating back, `filtersSignature` here can still be "{}" even if a
  // filter was active when the user left. If we compared and bailed permanently
  // on that first (premature) mismatch, the saved pagination payload would be
  // lost forever the moment any filter was applied.
  //
  // Instead: cache the raw payload once, then keep re-checking it against the
  // live `query`/`filtersSignature` as they settle, for a short grace window.
  // If they match within the window, restore. If the window expires without a
  // match, treat it as a genuine filter/search change and discard the payload.
  useEffect(() => {
    if (pendingRestorePayloadRef.current === undefined) {
      const stored = sessionStorage.getItem(storageKey);
      if (!stored) {
        pendingRestorePayloadRef.current = null;
        isRestoringRef.current = false;
        return;
      }
      try {
        pendingRestorePayloadRef.current = JSON.parse(stored);
      } catch (error) {
        pendingRestorePayloadRef.current = null;
        isRestoringRef.current = false;
        return;
      }
    }

    const parsed = pendingRestorePayloadRef.current;
    if (!parsed) return;

    const matches = parsed?.query === query && parsed?.filtersSignature === filtersSignature;

    if (matches) {
      if (restoreGraceTimerRef.current) {
        clearTimeout(restoreGraceTimerRef.current);
        restoreGraceTimerRef.current = null;
      }

      isRestoringRef.current = true;

      setListData(parsed?.listData || []);
      setTotalCount(parsed?.totalCount || 0);
      setHasMore(parsed?.hasMore ?? true);
      setPage(parsed?.page || 1);
      loadedPagesRef.current = new Set(parsed?.loadedPages || []);

      pendingScrollIdRef.current = parsed?.clickedItemId || null;

      hasRestoredStateRef.current = true;
      pendingRestorePayloadRef.current = null; // consumed — won't re-apply on later changes
      // isRestoringRef stays true on purpose. It's cleared in the effect below,
      // only once the restored `page` value has actually committed to state.
      return;
    }

    // Mismatch: could be a genuine filter/search change, or just the premature
    // "{}" signature before Filters.jsx finishes its own async restore. Give it
    // one short grace window (covers that async restore) before giving up.
    if (!restoreGraceTimerRef.current) {
      isRestoringRef.current = true; // hold real fetches off while we wait to find out
      restoreGraceTimerRef.current = setTimeout(() => {
        pendingRestorePayloadRef.current = null;
        isRestoringRef.current = false;
        restoreGraceTimerRef.current = null;
      }, 400); // comfortably longer than Filters.jsx's synchronous restore tick
    }
  }, [storageKey, query, filtersSignature]);

  // Clean up a pending grace timer if the component unmounts mid-wait.
  useEffect(() => {
    return () => {
      if (restoreGraceTimerRef.current) {
        clearTimeout(restoreGraceTimerRef.current);
      }
    };
  }, []);

  // Clear the restoring flag only after the restored `page` has committed.
  useEffect(() => {
    if (isRestoringRef.current) {
      isRestoringRef.current = false;
    }
  }, [page]);

  // Persist state function, used on unmount and right before navigating away.
  const persistState = useCallback(() => {
    const payload = {
      ...latestStateRef.current,
      loadedPages: Array.from(loadedPagesRef.current),
      clickedItemId: clickedItemIdRef.current,
    };
    sessionStorage.setItem(storageKey, JSON.stringify(payload));
  }, [storageKey]);

  // Ensure state is saved if the component unmounts for any reason.
  useEffect(() => {
    return () => {
      persistState();
    };
  }, [persistState]);

  // Execute DOM scroll restoration once the restored list has rendered.
  useEffect(() => {
    if (listData.length === 0 || !pendingScrollIdRef.current) return;

    const targetId = pendingScrollIdRef.current;
    pendingScrollIdRef.current = null; // clear immediately, only attempt once per restore

    let attempts = 0;
    const maxAttempts = 10;

    const restore = () => {
      const element = document.getElementById(`list-item-${targetId}`);
      if (element) {
        element.scrollIntoView({ behavior: "instant", block: "center" });
        return;
      }
      attempts += 1;
      if (attempts < maxAttempts) {
        setTimeout(restore, 150);
      }
    };

    restore();
  }, [listData.length]);

  // Trigger the debounced fetch whenever fetchList's identity changes (page, filters, query, etc.)
  // Never fires while a restore is still in flight.
  useEffect(() => {
    if (isRestoringRef.current) {
      return;
    }
    const handler = setTimeout(() => {
      fetchList();
    }, 300);
    return () => clearTimeout(handler);
  }, [fetchList]);

  // Reset pagination when query or filters change, UNLESS we just restored state
  // for them, OR a restore attempt is still pending (inside its grace window).
  useEffect(() => {
    if (hasRestoredStateRef.current) {
      hasRestoredStateRef.current = false;
      return;
    }
    if (pendingRestorePayloadRef.current !== null && pendingRestorePayloadRef.current !== undefined) {
      // A restore is still pending a match (grace window in progress) — don't
      // wipe sessionStorage or reset state out from under it.
      return;
    }

    clickedItemIdRef.current = null;
    pendingScrollIdRef.current = null;
    sessionStorage.removeItem(storageKey);
    loadedPagesRef.current = new Set();
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, filtersSignature]);

  // Infinite scroll observer.
  useEffect(() => {
    if (!loaderRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isFetchingMore && listData.length > 0) {
          setPage((prev) => prev + 1);
        }
      },
      { threshold: 1 },
    );
    observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [hasMore, isFetchingMore, listData.length]);

  // Call this with the clicked item's id right before navigating to its detail page.
  const handleItemClick = useCallback(
    (itemId) => {
      clickedItemIdRef.current = itemId;
      persistState();
    },
    [persistState],
  );

  return {
    listData,
    setListData, // exposed for optimistic local updates (e.g. wishlist toggle) that shouldn't trigger a refetch
    totalCount,
    page,
    loading,
    isFetchingMore,
    loaderRef,
    handleItemClick,
    resetList,
    // exposed so callers needing a one-off "initialized" check (e.g. customer
    // switcher) can hook into the same restore lifecycle without duplicating it.
    hasRestoredStateRef,
  };
}